import { BarSeries, CandlestickSeries, createChart, createSeriesMarkers, HistogramSeries, LineSeries } from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';

/** Ensure timestamp is treated as UTC if missing timezone info */
const toUtcTimestamp = (ts) => {
    if (!ts) return 0;
    let s = String(ts);
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[Z+\-]\d{0,4}:?\d{0,2}$/.test(s)) {
        s += 'Z';
    }
    return Math.floor(new Date(s).getTime() / 1000);
};

const TradingViewChart = ({ symbol, trades, onClose }) => {
    const chartContainerRef = useRef();
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('30m'); // '15m', '30m', '1H', '1D'
    const [showSMA, setShowSMA] = useState(true);
    const [chartType, setChartType] = useState('candle'); // 'candle', 'line', 'bar'
    const [showVolume, setShowVolume] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNoData(false);
        if (!chartContainerRef.current) return;
        
        const chart = createChart(chartContainerRef.current, {
            layout: { 
                background: { color: '#1e293b' },
                textColor: '#94a3b8',
            },
            grid: {
                vertLines: { color: '#334155' },
                horzLines: { color: '#334155' },
            },
            crosshair: { mode: 1 },
            rightPriceScale: { borderColor: '#334155' },
            timeScale: {
                borderColor: '#334155',
                timeVisible: true,
                secondsVisible: false,
            },
            localization: {
                timeFormatter: (bizDayOrTimestamp) => {
                    if (chartType === '1D') return new Date(bizDayOrTimestamp * 1000).toLocaleDateString();
                    return new Date(bizDayOrTimestamp * 1000).toLocaleString('en-US', { 
                        hour: '2-digit', minute: '2-digit', hour12: false
                    });
                }
            },
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
        });

        let mainSeries;
        if (chartType === 'line') {
            mainSeries = chart.addSeries(LineSeries, {
                color: '#3b82f6',
                lineWidth: 2,
            });
        } else if (chartType === 'bar') {
            mainSeries = chart.addSeries(BarSeries, {
                upColor: '#10b981',
                downColor: '#ef4444',
            });
        } else {
            mainSeries = chart.addSeries(CandlestickSeries, {
                upColor: '#10b981',
                downColor: '#ef4444',
                borderVisible: false,
                wickUpColor: '#10b981',
                wickDownColor: '#ef4444'
            });
        }

        const setupChartData = async () => {
            let historicalData = [];
            let volumeData = [];

            // Calculate position date range from trades for API request
            let positionStartMs = null;
            let positionEndMs = null;
            let accountId = null;
            if (trades && trades.length > 0) {
                accountId = trades[0].brokerAccountId || trades[0].broker_account_id;
                const tradeTimes = trades
                    .map(t => new Date(t.entryTime || t.entry_time || t.exitTime || t.exit_time || 0).getTime())
                    .filter(t => t > 0);
                if (tradeTimes.length > 0) {
                    // Add 1-day padding before first trade and after last trade
                    positionStartMs = Math.min(...tradeTimes) - 86400000;
                    positionEndMs = Math.max(...tradeTimes) + 86400000;
                    // Don't go past now
                    positionEndMs = Math.min(positionEndMs, Date.now());
                }
            }

            if (accountId) {
                try {
                    let url = `/broker-accounts/${accountId}/price-history/${symbol}?timeframe=${timeframe}`;
                    if (positionStartMs && positionEndMs) {
                        url += `&startDate=${positionStartMs}&endDate=${positionEndMs}`;
                    }
                    const response = await api.get(url);
                    if (response.data?.success && response.data?.data && response.data.data.length > 0) {
                         const rawData = response.data.data;
                         volumeData = rawData.map(d => ({
                             time: d.time,
                             value: d.volume,
                             color: d.close >= d.open ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'
                         }));
                         
                         historicalData = chartType === 'line' 
                             ? rawData.map(d => ({ time: d.time, value: d.close })) 
                             : rawData;
                    }
                } catch (err) {
                    console.warn("API price history failed, falling back to mock generator", err);
                }
            }

            if (historicalData.length === 0) {
                setNoData(true);
                setLoading(false);
                return;
            }

            mainSeries.setData(historicalData);

            let volumeSeries = null;
            if (showVolume) {
                volumeSeries = chart.addSeries(HistogramSeries, {
                    color: '#26a69a',
                    priceFormat: { type: 'volume' },
                    priceScaleId: '', // Set as an overlay
                });
                volumeSeries.priceScale().applyOptions({
                    scaleMargins: { top: 0.8, bottom: 0 },
                });
                volumeSeries.setData(volumeData);
            }

            let smaSeries = null;
            if (showSMA && historicalData.length > 0) {
                const smaData = [];
                const period = 20;
                for (let i = 0; i < historicalData.length; i++) {
                    if (i < period - 1) continue;
                    let sum = 0;
                    for (let j = 0; j < period; j++) {
                        const price = chartType === 'line' ? historicalData[i - j].value : historicalData[i - j].close;
                        sum += price;
                    }
                    smaData.push({ time: historicalData[i].time, value: sum / period });
                }
                
                smaSeries = chart.addSeries(LineSeries, {
                    color: '#f59e0b', // distinct orange for SMA
                    lineWidth: 2,
                    crosshairMarkerVisible: false,
                    priceLineVisible: false,
                });
                smaSeries.setData(smaData);
            }

            const markers = [];
            const getClosestCandleTime = (targetTs) => {
                if (!historicalData.length) return targetTs;
                let closest = historicalData[0].time;
                let minDiff = Math.abs(targetTs - closest);
                for(let i=1; i<historicalData.length; i++) {
                    let diff = Math.abs(targetTs - historicalData[i].time);
                    if (diff < minDiff) { 
                        minDiff = diff; 
                        closest = historicalData[i].time; 
                    }
                }
                return closest;
            };

            let offChartCount = 0;
            if (trades && trades.length > 0) {
                const chartStart = historicalData.length > 0 ? historicalData[0].time : 0;
                const chartEnd = historicalData.length > 0 ? historicalData[historicalData.length - 1].time : Infinity;

                trades.forEach(trade => {
                    const side = trade.side?.toUpperCase() || "";
                    const isBuy = side === "BUY" || side === "BUY_TO_COVER" || side === "BUY_TO_OPEN" || side === "BUY_TO_CLOSE";

                    // Each trade leg has one execution: entryTime is the execution time
                    const execTime = trade.entryTime || trade.entry_time || trade.exitTime || trade.exit_time;
                    if (!execTime) return;

                    const ts = toUtcTimestamp(execTime);
                    const isOffChart = ts < chartStart || ts > chartEnd;
                    if (isOffChart) offChartCount++;

                    const snappedTime = getClosestCandleTime(ts);
                    let label = isBuy ? 'BUY' : 'SELL';
                    if (side === "SHORT" || side === "SELL_SHORT") label = "SHORT";
                    if (isOffChart) label += '*';

                    markers.push({
                        time: snappedTime,
                        position: isBuy ? 'belowBar' : 'aboveBar',
                        color: isBuy ? '#10b981' : '#ef4444',
                        shape: isBuy ? 'arrowUp' : 'arrowDown',
                        text: label,
                    });
                });
            }

            markers.sort((a,b) => a.time - b.time);

            // Deduplicate markers at the same candle time — keep first of each time
            // (lightweight-charts requires unique times)
            const dedupedMarkers = [];
            const seenTimes = new Set();
            for (const m of markers) {
                if (!seenTimes.has(m.time)) {
                    seenTimes.add(m.time);
                    dedupedMarkers.push(m);
                }
            }

            try {
                if (dedupedMarkers.length > 0) createSeriesMarkers(mainSeries).setMarkers(dedupedMarkers);
            } catch (e) {
                console.warn('Failed to set markers:', e);
            }

            // Show warning if some trade markers are outside chart data range
            if (offChartCount > 0) {
                const warning = document.createElement('div');
                warning.style.cssText = 'position:absolute;bottom:40px;left:50%;transform:translateX(-50%);z-index:1000;padding:6px 14px;background:rgba(234,179,8,0.15);border:1px solid #ca8a04;border-radius:6px;color:#fbbf24;font-size:11px;pointer-events:none;white-space:nowrap;';
                warning.textContent = `${offChartCount} trade(s) marked with * are outside chart range — try a wider timeframe`;
                chartContainerRef.current.appendChild(warning);
            }

            const tooltip = document.createElement('div');
            tooltip.style.position = 'absolute';
            tooltip.style.top = '12px';
            tooltip.style.left = '12px';
            tooltip.style.zIndex = '1000';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.color = '#cbd5e1';
            tooltip.style.fontSize = '12px';
            tooltip.style.fontFamily = 'monospace';
            tooltip.style.backgroundColor = 'rgba(15, 23, 42, 0.8)';
            tooltip.style.padding = '10px';
            tooltip.style.borderRadius = '6px';
            tooltip.style.border = '1px solid #334155';
            tooltip.style.display = 'none';
            chartContainerRef.current.appendChild(tooltip);

            chart.subscribeCrosshairMove(param => {
                if (
                    param.point === undefined ||
                    !param.time ||
                    param.point.x < 0 ||
                    param.point.x > chartContainerRef.current.clientWidth ||
                    param.point.y < 0 ||
                    param.point.y > chartContainerRef.current.clientHeight
                ) {
                    tooltip.style.display = 'none';
                } else {
                    const dateStr = new Date(param.time * 1000).toLocaleString('en-US', { 
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', hour12: true
                    });
                    const data = param.seriesData.get(mainSeries);
                    const vol = volumeSeries ? param.seriesData.get(volumeSeries) : null;
                    const sma = smaSeries ? param.seriesData.get(smaSeries) : null;
                    if (data) {
                        tooltip.style.display = 'block';
                        tooltip.replaceChildren();

                        const setEl = (el, text, styles = {}) => {
                            el.textContent = text;
                            Object.assign(el.style, styles);
                            return el;
                        };

                        tooltip.appendChild(setEl(document.createElement('div'), dateStr, { marginBottom: '6px', color: '#94a3b8', fontWeight: '600' }));

                        if (chartType === 'line') {
                            tooltip.appendChild(setEl(document.createElement('div'), `Price: ${data.value.toFixed(2)}`, { fontWeight: 'bold', color: '#3b82f6' }));
                        } else {
                            const color = data.close >= data.open ? '#10b981' : '#ef4444';
                            const grid = document.createElement('div');
                            Object.assign(grid.style, { display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' });
                            [['O', data.open], ['H', data.high], ['L', data.low], ['C', data.close]].forEach(([label, val]) => {
                                const cell = document.createElement('div');
                                cell.textContent = `${label}: `;
                                const span = document.createElement('span');
                                span.textContent = val.toFixed(2);
                                span.style.fontWeight = 'bold';
                                if (label === 'C') span.style.color = color;
                                cell.appendChild(span);
                                grid.appendChild(cell);
                            });
                            tooltip.appendChild(grid);
                        }

                        const footer = document.createElement('div');
                        Object.assign(footer.style, { marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #334155' });
                        if (vol) footer.appendChild(setEl(document.createElement('div'), `Vol: ${vol.value.toLocaleString()}`));
                        if (sma) footer.appendChild(setEl(document.createElement('div'), `SMA (20): ${sma.value.toFixed(2)}`, { color: '#f59e0b' }));
                        tooltip.appendChild(footer);
                    }
                }
            });

            setLoading(false);
        };

        setupChartData();

        const handleResize = () => {
            if (chartContainerRef.current) {
                chart.applyOptions({
                    width: chartContainerRef.current.clientWidth,
                    height: chartContainerRef.current.clientHeight
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
        };
    }, [symbol, trades, timeframe, showSMA, chartType, showVolume]); // rebuild heavily on options changed instead of tearing state manually

    return (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-2xl" style={{ width: '90%', height: '80%', display: 'flex', flexDirection: 'column' }}>
                
                {/* Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-slate-800 bg-slate-900/50 backdrop-blur-md">
                   <h3 className="text-slate-200 font-semibold">{symbol} • Contextual Position Review</h3>
                   <button onClick={onClose} className="text-slate-400 hover:text-white pb-1 text-2xl leading-none">&times;</button>
                </div>

                {/* Toolbar */}
                <div className="flex items-center gap-4 px-4 py-2 border-b border-slate-800 bg-slate-800/30 text-xs text-slate-300">
                    <div className="flex bg-slate-800 rounded-md p-0.5 border border-slate-700">
                        {['1m', '5m', '15m', '30m', '1H', '1D'].map(tf => (
                            <button 
                                key={tf}
                                onClick={() => setTimeframe(tf)}
                                className={`px-3 py-1 rounded ${timeframe === tf ? 'bg-slate-700 text-white font-medium shadow-sm' : 'hover:bg-slate-700/50 text-slate-400'}`}
                            >
                                {tf}
                            </button>
                        ))}
                    </div>

                    <div className="h-4 w-px bg-slate-700 mx-1"></div>

                    <div className="flex bg-slate-800 rounded-md p-0.5 border border-slate-700">
                        {[
                            { id: 'candle', label: '🕯️ Candles' },
                            { id: 'line', label: '📈 Line' },
                            { id: 'bar', label: '📊 Bars' }
                        ].map(type => (
                            <button 
                                key={type.id}
                                onClick={() => setChartType(type.id)}
                                className={`px-3 py-1 rounded ${chartType === type.id ? 'bg-slate-700 text-white font-medium shadow-sm' : 'hover:bg-slate-700/50 text-slate-400'}`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>

                    <div className="h-4 w-px bg-slate-700 mx-1"></div>
                    
                    <div className="flex gap-2">
                        <label className="flex items-center gap-1.5 cursor-pointer hover:text-white transition-colors">
                            <input 
                                type="checkbox" 
                                checked={showSMA} 
                                onChange={(e) => setShowSMA(e.target.checked)} 
                                className="rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-900"
                            />
                            <span>SMA (20)</span>
                        </label>
                        <label className="flex items-center gap-1.5 ml-2 cursor-pointer hover:text-white transition-colors">
                            <input 
                                type="checkbox" 
                                checked={showVolume} 
                                onChange={(e) => setShowVolume(e.target.checked)} 
                                className="rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500 focus:ring-offset-slate-900"
                            />
                            <span>Volume</span>
                        </label>
                    </div>
                </div>

                {/* Canvas Container */}
                <div style={{ flex: 1, position: 'relative' }} ref={chartContainerRef}>
                    {loading && <div className="absolute inset-0 flex items-center justify-center text-slate-400 z-50 bg-slate-900/50">Loading historical data...</div>}
                    {!loading && noData && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400 z-50 bg-slate-900/50 text-center px-6">
                            <span className="text-lg font-medium text-slate-300">No market data available for {symbol}</span>
                            <span className="text-sm max-w-md">
                                Price history is only available for equity symbols via a connected Schwab account.
                                Options and manually-entered symbols can't be charted here yet.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TradingViewChart;
